import type { OptimizationResult, JobData, FitAssessment } from '../types/optimization';
import { analyzeResumeDensity, extractContactDetails, isResumeComplete, extractKeyImprovements, identifySkillsGaps, extractAISuggestions, detectExperienceLevel } from './resumeAnalysisUtils';
import { calculateInitialScore, calculateOptimizedScore, calculateJobMatchScore, calculateOptimizedJobMatchScore, escapeRegExp } from './scoreCalculations';
import { postProcessResume } from './resumeProcessingUtils';
import { fixUtf8Mojibake } from './pdfTextSanitizer';
import { restoreResumeStructure } from './resumeStructure';

/**
 * Validates resume length with adaptive scaling
 * @param text Optimized resume text
 * @param originalText Original resume text
 * @param experienceLevel Detected experience level
 */
export function validateResumeLength(text: string, originalText: string, experienceLevel: string): void {
  const wordCount = text.split(/\s+/).length;
  const originalWordCount = originalText.split(/\s+/).length;
  
  const { density } = analyzeResumeDensity(originalText);
  const isDense = density > 12;
  const isSparse = density < 8;
  
  let minWords, maxWords;
  
  // Set bounds based on density and experience
  if (isSparse) {
    minWords = originalWordCount * 1.3;
    maxWords = originalWordCount * 1.9;
  } else if (isDense) {
    minWords = originalWordCount * 0.8;
    maxWords = originalWordCount * 1.2;
  } else {
    switch(experienceLevel) {
      case 'Senior': 
        minWords = Math.max(600, originalWordCount * 0.9);
        maxWords = Math.min(900, originalWordCount * 1.4);
        break;
      case 'Mid-Level': 
        minWords = Math.max(400, originalWordCount * 0.9);
        maxWords = Math.min(600, originalWordCount * 1.3);
        break;
      default: 
        minWords = Math.max(250, originalWordCount * 0.9);
        maxWords = Math.min(400, originalWordCount * 1.2);
    }
  }

  if (wordCount < minWords || wordCount > maxWords) {
    console.warn(`Resume length validation: ${wordCount} words (expected ${minWords}-${maxWords})`);
    console.log(`Density: ${density}, Experience: ${experienceLevel}, Original: ${originalWordCount}`);
  }
}

/**
 * Transforms raw LLM response text
 * @param rawText Raw text from LLM
 * @param experienceLevel Detected experience level
 * @param originalResume Original resume text
 * @returns Cleaned and transformed resume text
 */
export function transformResponse(rawText: string, experienceLevel: string, originalResume: string): string {
  let cleanedText = fixUtf8Mojibake(rawText);
  // Remove LLM commentary and markdown
  cleanedText = cleanedText
    .replace(/^.*(Here is|Optimized Resume|Generated Resume|Resume Optimization):?\s*/i, '')
    .replace(/```(json|markdown)?/g, '')
    .replace(/\*\*Note:\*\*.*$/i, '')
    .replace(/Note:.*$/i, '')
    .replace(/\(AI Suggestion:.*?\)/g, '')
    .trim();

  // Check if we have a complete resume
  if (!isResumeComplete(cleanedText)) {
    console.warn('Incomplete resume detected, attempting to recover');
    // Try to find the actual resume content
    const resumeMatch = cleanedText.match(/(.*?)(?:\n\s*(?:---|NOTE|WARNING|$))/is);
    if (resumeMatch && resumeMatch[1]) {
      cleanedText = resumeMatch[1].trim();
    }
  }

  const contact = extractContactDetails(originalResume);

  // Replace common placeholder contact block (John Doe, johndoe@email.com, etc.) with user's real contact
  const placeholderContactPatterns = [
    /\bJohn\s+Doe\b/i,
    /\bjane\s+doe\b/i,
    /johndoe@\w+\.\w+/i,
    /john@email\.com/i,
    /jane@example\.com/i,
    /\+91-9876543210/,
    /linkedin\.com\/in\/johndoe/i,
    /linkedin\.com\/in\/janedoe/i
  ];
  const hasPlaceholder = placeholderContactPatterns.some(p => p.test(cleanedText));
  if (hasPlaceholder) {
    const lines = cleanedText.split(/\n/);
    let contactEndIndex = 0;
    for (let i = 0; i < Math.min(lines.length, 12); i++) {
      const line = lines[i];
      if (placeholderContactPatterns.some(p => p.test(line)) || /^(Name|Email|Phone|LinkedIn|Portfolio):\s*/i.test(line)) {
        contactEndIndex = i + 1;
      } else if (contactEndIndex > 0 && line.trim() === '') {
        contactEndIndex = i + 1;
      } else if (contactEndIndex > 0 && /^\s*[A-Z][a-z]+(\s+[A-Z][a-z]+)*\s*$/.test(line.trim()) && line.trim().length < 50) {
        contactEndIndex = i + 1;
      } else if (contactEndIndex > 0) break;
    }
    if (contactEndIndex > 0) {
      const rest = lines.slice(contactEndIndex).join('\n').replace(/^\n+/, '');
      const userBlock = [
        contact.name,
        [contact.email, contact.phone].filter(Boolean).join(' | '),
        contact.linkedin ? `LinkedIn: ${contact.linkedin}` : '',
        contact.portfolio ? `Portfolio: ${contact.portfolio}` : ''
      ].filter(Boolean).join('\n');
      cleanedText = userBlock ? `${userBlock}\n\n${rest}` : rest;
    }
  }

  const contactPatterns = [
    contact.name ? contact.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') : '',
    contact.email ? contact.email.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') : '',
    contact.phone ? contact.phone.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') : '',
    contact.linkedin ? contact.linkedin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') : '',
    contact.portfolio ? contact.portfolio.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') : ''
  ].filter(Boolean);
  const contactRegex = contactPatterns.length ? new RegExp(`(${contactPatterns.join('|')})`, 'gi') : null;
  const hasOriginalContacts = contactRegex ? contactRegex.test(cleanedText) : false;

  if (!hasOriginalContacts && (contact.name || contact.email)) {
    const userBlock = [
      contact.name,
      [contact.email, contact.phone].filter(Boolean).join(' | '),
      contact.linkedin ? `LinkedIn: ${contact.linkedin}` : '',
      contact.portfolio ? `Portfolio: ${contact.portfolio}` : ''
    ].filter(Boolean).join('\n');
    return `${userBlock}\n\n${cleanedText}`.trim();
  }

  // Section preservation logic
  const requiredSections = ['experience', 'education', 'skills'];
  let finalOutput = cleanedText;
  
  requiredSections.forEach(section => {
    const sectionRegex = new RegExp(`(#\\s*${section}[\\s\\S]*?)(?=#|$)`, 'i');
    const originalMatch = originalResume.match(sectionRegex);
    const generatedMatch = cleanedText.match(sectionRegex);
    
    // Preserve original section if LLM dropped content
    if (originalMatch && !generatedMatch) {
      console.warn(`Restoring original ${section} section`);
      finalOutput += `\n\n${originalMatch[0]}`;
    }
  });
  
  return finalOutput;
}

/**
 * Creates a fit assessment based on match score
 * @param matchScore The calculated match score
 * @returns Fit assessment object
 */
function createFitAssessment(matchScore: number): FitAssessment {
  // Ensure matchScore is a valid number
  const validMatchScore = typeof matchScore === 'number' && !isNaN(matchScore) ? matchScore : 0;
  
  let level: 'high' | 'medium' | 'low';
  let reason: string;
  let warnings: string[] = [];
  
  if (validMatchScore >= 80) {
    level = 'high';
    reason = 'Your resume is well-aligned with this job posting';
  } else if (validMatchScore >= 60) {
    level = 'medium';
    reason = 'Your resume matches many requirements but could be improved';
    warnings.push('Consider adding more relevant keywords');
  } else {
    level = 'low';
    reason = 'Your resume needs significant improvements to match this job';
    warnings.push('Add more relevant skills and experience details');
    warnings.push('Tailor your professional summary to this role');
  }
  
  return {
    level,
    reason,
    score: validMatchScore,
    warnings: warnings.length > 0 ? warnings : undefined
  };
}

/**
 * Transforms Groq LLM response to optimization result
 * @param generatedText Generated resume text
 * @param originalText Original resume text
 * @param initialJobMatchScore Initial job match score
 * @param jobData Job data with user ID
 * @param processingTimeMs Processing time in milliseconds
 * @returns Structured optimization result
 */
export function transformGroqResponseToOptimizationResult(
  generatedText: string,
  originalText: string,
  initialJobMatchScore: number,
  jobData: JobData & { userId: string; resumeId?: string; linkId?: string },
  processingTimeMs?: number
): OptimizationResult {
  let generatedTextClean = fixUtf8Mojibake(generatedText);
  generatedTextClean = restoreResumeStructure(generatedTextClean);
  // Start processing timer if not provided
  const startTime = processingTimeMs ? Date.now() - processingTimeMs : Date.now();
  
  // Ensure initial job match score is a valid number
  const validInitialJobMatchScore = typeof initialJobMatchScore === 'number' && !isNaN(initialJobMatchScore) && initialJobMatchScore > 0 
    ? initialJobMatchScore 
    : calculateJobMatchScore(originalText, jobData.skills || [], jobData.description || '');
  
  // Validate we have a proper resume structure
  const sectionValidation = {
    hasExperience: /(work|experience|employment)/i.test(generatedTextClean),
    hasEducation: /(education|academic)/i.test(generatedTextClean),
    hasSkills: /(skills|competencies)/i.test(generatedTextClean)
  };

  // Collect warnings instead of throwing errors
  const warnings: string[] = [];
  
  if (!sectionValidation.hasExperience) {
    console.warn('Resume missing Experience section');
    warnings.push('Missing Experience section in resume');
  }
  
  if (!sectionValidation.hasEducation) {
    console.warn('Resume missing Education section');
    warnings.push('Missing Education section in resume');
  }
  
  if (!sectionValidation.hasSkills) {
    console.warn('Resume missing Skills section');
    warnings.push('Missing Skills section in resume');
  }

  // Content scaling analysis
  const origWordCount = originalText.split(/\s+/).length;
  const optWordCount = generatedTextClean.split(/\s+/).length;
  const wordCountChange = Math.round(((optWordCount - origWordCount) / origWordCount) * 100);
  const densityChange = Math.round((analyzeResumeDensity(generatedTextClean).density - analyzeResumeDensity(originalText).density) * 100) / 100;

  // Calculate optimized job match score
  const optimizedJobMatchScore = calculateOptimizedJobMatchScore(
    validInitialJobMatchScore,
    originalText,
    generatedTextClean,
    jobData
  );

  // Ensure scores are valid numbers
  const finalInitialJobMatchScore = typeof validInitialJobMatchScore === 'number' && !isNaN(validInitialJobMatchScore) ? validInitialJobMatchScore : 65;
  const finalOptimizedJobMatchScore = typeof optimizedJobMatchScore === 'number' && !isNaN(optimizedJobMatchScore) ? optimizedJobMatchScore : finalInitialJobMatchScore + 10;

  // DEBUG LOGGING: Score calculation results
  console.log('[SCORE] Calculation debug:', {
    originalInitialJobMatchScore: initialJobMatchScore,
    validInitialJobMatchScore,
    finalInitialJobMatchScore,
    calculatedOptimizedJobMatchScore: optimizedJobMatchScore,
    finalOptimizedJobMatchScore,
    improvement: finalOptimizedJobMatchScore - finalInitialJobMatchScore,
    jobTitle: jobData.title,
    company: jobData.company
  });

  // Extract improvements
  const improvements = extractKeyImprovements(originalText, generatedTextClean);
  
  // Extract skills gaps
  const skillsGaps = identifySkillsGaps(generatedTextClean, jobData.skills);

  // Preserve user contact and replace placeholder data (e.g. John Doe) with real data from original resume
  const experienceLevelForTransform = detectExperienceLevel(originalText, jobData);
  const contactPreservedText = transformResponse(generatedTextClean, experienceLevelForTransform, originalText);
  const optimizedResume = contactPreservedText;
  const finalResume = postProcessResume(contactPreservedText);

  // Prepare AI insights
  const aiInsights = {
    wordCountChange,
    densityChange,
    sectionValidation,
    keywordMatches: jobData.skills?.filter(skill =>
      new RegExp(`\\b${escapeRegExp(skill)}\\b`, 'i').test(generatedTextClean)
    ).length || 0,
    totalKeywords: jobData.skills?.length || 0
  };
  
  // Create fit assessment with validated job match score
  const fitAssessment = createFitAssessment(finalOptimizedJobMatchScore);

  // Measure processing time
  const actualProcessingTimeMs = processingTimeMs || (Date.now() - startTime);
  
  // Detect experience level
  const experienceLevel = detectExperienceLevel(generatedTextClean, jobData);
  
  // Extract AI suggestions
  const suggestions = extractAISuggestions(generatedTextClean);
  
  // Build the result object
  const result = {
    optimization_id: null,
    user_id: jobData.userId,
    initial_score: finalInitialJobMatchScore,
    match_score: finalOptimizedJobMatchScore,
    optimized_score: finalOptimizedJobMatchScore,
    ai_insights: aiInsights,
    suggestions: suggestions,
    skills_gaps: skillsGaps,
    created_at: new Date().toISOString(),
    link_id: jobData.linkId || null,
    resume_id: jobData.resumeId || null,
    job_id: jobData.jobId || null,
    fit_assessment: fitAssessment,
    final_resume: finalResume,
    optimized_resume: optimizedResume,
    improvements: improvements,
    processing_time_ms: actualProcessingTimeMs,
    experience_level: experienceLevel,
    optimization_date: new Date().toISOString(),
    
    // These fields are used by the frontend
    // Additional fields for frontend compatibility
    originalScore: finalInitialJobMatchScore,
    optimizedScore: finalOptimizedJobMatchScore,
    jobTitle: jobData.title,
    companyName: jobData.company,
    jobUrl: jobData.jobUrl, 
    jobIndex: jobData.jobIndex,
    warnings: warnings.length > 0 ? warnings : undefined,
    optimizedResume: optimizedResume,
    skillGaps: skillsGaps // Ensure skillGaps is also available for frontend
  } as OptimizationResult;
  
  return result;
}
